import { getSourceValue, resolvePath } from "../../../layout/resolution.js";
import { DataSourceId, InjectedData } from "../../../layout/types.js";
import { EventNode } from "../../types.js";
import { resolveShortcutCombo } from "../../shortcut-keys.js";
import {
  NodeExecutionResult,
  BehaviorFlow,
  ExecutionContext,
} from "../types.js";
import { evaluateCustomEventTrigger } from "../custom-event-runtime.js";
import { emptyPatchResult, stableSerialize } from "../helpers.js";
import { BehaviorNode } from "./BehaviorNode.js";

export class EventBehaviorNode extends BehaviorNode {
  declare behavior: EventNode;
  constructor(behavior: EventNode) {
    super(behavior);
  }

  execute(context: ExecutionContext): NodeExecutionResult {
    return {
      patch: emptyPatchResult(),
      nextFlows: this.getNextFlows(context),
      scheduledNodes: [],
      runtimeSignals: [],
    };
  }
  getOutput(
    _portId: string,
    _visiting: Set<string>,
    _context: ExecutionContext,
  ): unknown {
    return undefined;
  }
  getNextFlows(context: ExecutionContext): BehaviorFlow[] {
    return (this.outgoingEdgesByPort.get("output") ?? []).map((edge) => ({
      edge,
      context,
    }));
  }

  resolveWatchedValue(data: InjectedData): unknown {
    if (!this.behavior.objectSource) return undefined;
    const source = getSourceValue(
      data,
      this.behavior.objectSource as DataSourceId,
    );
    if (this.behavior.objectFieldPath) {
      return resolvePath(source, this.behavior.objectFieldPath);
    }
    return source;
  }

  shouldFire(context: ExecutionContext): boolean {
    switch (this.behavior.kind) {
      case "onLoad":
        return !context.previousData;
      case "onClick":
        return context.customDisciplineSignal === "onClick";
      case "onChange": {
        if (!context.previousData) {
          return Boolean(this.behavior.triggerOnInit);
        }
        const next = stableSerialize(this.resolveWatchedValue(context.data));
        const prev = context.previousData
          ? stableSerialize(this.resolveWatchedValue(context.previousData))
          : undefined;
        return prev !== next && prev !== undefined;
      }
      case "onListEnter":
      case "onListExit":
      case "onListFilterExit":
      case "onListFilterEnter": {
        const current = context.listTransitionSignal;
        const prev = context.previousData?.listTransitionSignal;
        return current === this.behavior.kind && prev !== this.behavior.kind;
      }
      case "onControlRoom": {
        const signals = context.controlRoomSignals;
        if (!signals || signals.length === 0) return false;
        const combo = resolveShortcutCombo(
          this.behavior,
          context.shortcutPreferences,
        );
        if (!combo) return false;
        return signals.includes(combo);
      }
      case "onCustomSceneEvent": {
        const eventId = this.behavior.customEventId;
        if (!eventId) return false;
        return evaluateCustomEventTrigger(eventId, context);
      }
      default:
        return false;
    }
  }
}
